import { ChakraProvider, Box, VStack, Container, Button, Text, HStack, Flex } from '@chakra-ui/react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import theme from '../theme';
import AudioPlayer from '../components/AudioPlayer';
import EmbedTransportControls from '../components/EmbedTransportControls';
import { AudioProvider, useAudioContext } from '../context/AudioContext';
import { SharedDataProvider, useSharedData } from '../context/SharedDataContext';
import { NotesProvider, useNotes } from '../context/NotesContext';
import { DisplayNamesProvider, useDisplayNames } from '../context/DisplayNamesContext';
import { PageTitleProvider } from '../context/PageTitleContext';
import { FileService, LoadedFile, ShareData } from '../services/FileService';
import { SharingService } from '../services/SharingService';
import LoadingSpinner from '../components/LoadingSpinner';
import { SyncProvider, useSync } from '../features/sync/context/SyncContext';
import { FaExternalLinkAlt } from 'react-icons/fa';
import LZString from 'lz-string';
import { ensureAudioContextResumed, initializeMobileAudioContext } from '../utils/mobileAudioContextManager';
import { isMobileDevice } from '../utils/deviceDetection';
import Logo from '../components/Logo';
import WaveSurfer from 'wavesurfer.js';
import { matchAudioLevels } from '../utils/levelMatching';
import { simplifyLoadedFileFilenames } from '../utils/filenameSimplificationHelper';
import { formatLoadingMessage } from '../utils/loadingMessageFormatter';

const EmbedContent = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSetupLoaded, setIsSetupLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedFiles, setLoadedFiles] = useState<LoadedFile[]>([]);
  const [fullSetupUrl, setFullSetupUrl] = useState<string>('');
  const [parsedShareData, setParsedShareData] = useState<ShareData | null>(null);
  const [sharedProgress, setSharedProgress] = useState<{ completed: number; total: number }>({ completed: 0, total: 0 });
  const [sharedDownloadedBytes, setSharedDownloadedBytes] = useState(0);
  const [sharedTotalBytes, setSharedTotalBytes] = useState(0);
  const { shareData } = useSharedData(); // Get shareData from context instead of local state
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    isPlaying,
    playAll,
    pauseAll,
    forward,
    rewind,
    rewindToLastStart,
    selectedPlayer,
    audioElements,
    setGainOffsetsWithFileMapping,
  } = useAudioContext();
  
  const { migrateDisplayNamesFromTrackNumbers } = useDisplayNames();
  const { migrateNotesFromTrackNumbers } = useNotes();
  
  // Level matching state and refs (similar to App.tsx)
  const wavesurferRefs = useRef<Map<number, WaveSurfer>>(new Map());
  const [readyPlayers, setReadyPlayers] = useState<Set<number>>(new Set());
  const [playersGeneration, setPlayersGeneration] = useState(0);
  const levelMatchingAppliedRef = useRef<number>(0);
  
  // Migrate display names and notes from track numbers to file IDs when files are loaded
  // DisplayNamesContext and NotesContext already load from shareData automatically
  useEffect(() => {
    if (loadedFiles.length > 0) {
      migrateDisplayNamesFromTrackNumbers(loadedFiles);
      migrateNotesFromTrackNumbers(loadedFiles);
    }
  }, [loadedFiles, migrateDisplayNamesFromTrackNumbers, migrateNotesFromTrackNumbers]);

  // Parse share data from URL (without loading files)
  const parseEmbedData = useCallback(() => {
    try {
      setIsLoading(true);
      setError(null);

      // Get data from URL params
      const dataParam = searchParams.get('data');
      if (!dataParam) {
        throw new Error('No embed data provided');
      }

      // Decompress and parse share data
      const decompressedData = LZString.decompressFromEncodedURIComponent(dataParam);
      if (!decompressedData) {
        throw new Error('Failed to decompress embed data');
      }

      const parsedData: ShareData = JSON.parse(decompressedData);
      
      // Validate share data
      if (!SharingService.isValidShareData(parsedData)) {
        throw new Error('Invalid embed data format');
      }

      setParsedShareData(parsedData);

      // Generate full setup URL for "View Full Setup" button
      const compressedData = LZString.compressToEncodedURIComponent(JSON.stringify(parsedData));
      const baseUrl = window.location.origin + window.location.pathname.replace('/embed', '');
      setFullSetupUrl(`${baseUrl}?data=${compressedData}`);
    } catch (err) {
      console.error('[Embed] Error parsing embed data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load embed');
    } finally {
      setIsLoading(false);
    }
  }, [searchParams]);

  // Load actual audio files (only when user clicks "Load Setup")
  const loadEmbedFiles = useCallback(async () => {
    console.log('[Embed] loadEmbedFiles called - user clicked Load Setup button');
    try {
      setIsLoading(true);
      setError(null);

      // Get data from URL params
      const dataParam = searchParams.get('data');
      if (!dataParam) {
        throw new Error('No embed data provided');
      }

      console.log('[Embed] Starting to load files via FileService.loadSharedSetup');
      // Reset progress tracking
      setSharedProgress({ completed: 0, total: 0 });
      setSharedDownloadedBytes(0);
      setSharedTotalBytes(0);
      
      // Load files using FileService static method with progress callbacks
      const { files } = await FileService.loadSharedSetup(
        dataParam,
        (completed, total) => setSharedProgress({ completed, total }), // onProgress
        (downloadedBytes, totalBytes) => {
          setSharedDownloadedBytes(downloadedBytes);
          setSharedTotalBytes(totalBytes);
        }  // onBytesProgress
      );
      
      if (files.length === 0) {
        throw new Error('No files could be loaded');
      }

      // Simplify filenames for direct link files
      const simplifiedFiles = simplifyLoadedFileFilenames(files);
      
      setLoadedFiles(simplifiedFiles);
      setIsSetupLoaded(true);
      setPlayersGeneration(g => g + 1); // Increment generation on file load
      setReadyPlayers(new Set()); // Reset ready players when new files are loaded
      wavesurferRefs.current.clear(); // Clear WaveSurfer refs
      
      // Reset progress tracking
      setSharedProgress({ completed: 0, total: 0 });
      setSharedDownloadedBytes(0);
      setSharedTotalBytes(0);
    } catch (err) {
      console.error('[Embed] Error loading embed files:', err);
      setError(err instanceof Error ? err.message : 'Failed to load embed');
      
      // Reset progress tracking on error
      setSharedProgress({ completed: 0, total: 0 });
      setSharedDownloadedBytes(0);
      setSharedTotalBytes(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchParams]);

  // Parse data on mount (but don't load files yet)
  useEffect(() => {
    console.log('[Embed] Component mounted, parsing embed data (NOT loading files)');
    parseEmbedData();
  }, [parseEmbedData]);

  // Defensive check: Ensure files are never loaded automatically
  useEffect(() => {
    if (loadedFiles.length > 0 && !isSetupLoaded) {
      console.error('[Embed] ERROR: Files were loaded without user clicking button! Clearing files.');
      setLoadedFiles([]);
    }
  }, [loadedFiles, isSetupLoaded]);

  // Log when placeholder is shown
  useEffect(() => {
    if (!isSetupLoaded && !isLoading && parsedShareData) {
      console.log('[Embed] Placeholder shown - waiting for user to click "Load Setup" button');
    }
  }, [isSetupLoaded, isLoading, parsedShareData]);

  // Handle player ready callback for level matching
  const handlePlayerReady = useCallback((wavesurfer: WaveSurfer, playerNumber: number) => {
    console.log(`[Embed] [Player Ready] Player ${playerNumber} ready (${wavesurfer.getDuration()}s)`);
    wavesurferRefs.current.set(playerNumber, wavesurfer);
    setReadyPlayers(prev => {
      const newSet = new Set(prev);
      newSet.add(playerNumber);
      return newSet;
    });
  }, []);

  // Level matching effect - runs when all players are ready
  useEffect(() => {
    console.log(`[Embed] [Level Matching] ${readyPlayers.size}/${loadedFiles.length} players ready`);
    
    // Only run level matching if all players are ready AND we haven't applied it for this generation of players
    if (readyPlayers.size === loadedFiles.length && 
        loadedFiles.length > 0 && 
        levelMatchingAppliedRef.current !== playersGeneration) {
      console.log('[Embed] [Level Matching] All players ready, running level matching...');
      levelMatchingAppliedRef.current = playersGeneration; // Mark as applied for this generation
      
      const applyLevelMatching = async () => {
        await new Promise(resolve => setTimeout(resolve, 100));
        const results = await matchAudioLevels(wavesurferRefs.current);
        
        // Create offset map using sequential player numbers (1, 2, 3...) to match renumbering system
        const offsetMap = new Map<number, number>();
        const sortedResults = results.sort((a, b) => a.playerNumber - b.playerNumber);
        sortedResults.forEach((result, index) => {
          const sequentialPlayerNumber = index + 1;
          offsetMap.set(sequentialPlayerNumber, result.gainReduction);
        });
        
        console.log('[Embed] [Level Matching] Applied gain offsets:', Object.fromEntries(offsetMap));
        console.log('[Embed] [Level Matching] Current selected player:', selectedPlayer);
        setGainOffsetsWithFileMapping(new Map(offsetMap), loadedFiles);
        console.log('[Embed] [Level Matching] Gain offsets have been applied to audio context');
      };
      
      applyLevelMatching().catch(error => {
        console.error('[Embed] [Level Matching] Error:', error);
      });
    }
  }, [readyPlayers, loadedFiles.length, playersGeneration, setGainOffsetsWithFileMapping, selectedPlayer, loadedFiles]);

  // Show initial loading spinner only during initial parse (when parsedShareData doesn't exist yet)
  if (isLoading && !isSetupLoaded && !parsedShareData) {
    return (
      <Box
        w="100%"
        h="100vh"
        minH="400px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.900"
        borderRadius="2xl"
        sx={{
          borderRadius: '16px',
        }}
      >
        <LoadingSpinner message="Loading player..." />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        w="100%"
        h="100vh"
        minH="400px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.900"
        borderRadius="2xl"
        p={4}
        sx={{
          borderRadius: '16px',
        }}
      >
        <VStack spacing={4}>
          <Text color="red.400" fontSize="lg" textAlign="center">
            Error: {error}
          </Text>
          <Text color="gray.400" fontSize="sm" textAlign="center">
            The embed could not be loaded. Please check the embed code.
          </Text>
        </VStack>
      </Box>
    );
  }

  // Show placeholder if setup hasn't been loaded yet
  // If isLoading is true here, it means files are being loaded (after button click)
  if (!isSetupLoaded) {
    const trackCount = parsedShareData?.tracks?.length || 0;
    return (
      <Flex
        direction="column"
        h="100vh"
        w="100%"
        bg="gray.900"
        overflow="hidden"
        position="relative"
        alignItems="center"
        justifyContent="center"
        borderRadius="2xl"
        sx={{
          borderRadius: '16px',
        }}
      >
        {isLoading ? (
          <LoadingSpinner 
            message={formatLoadingMessage('Loading setup...', {
              totalBytes: sharedTotalBytes,
              downloadedBytes: sharedDownloadedBytes,
              progress: sharedProgress,
            })}
          />
        ) : (
          <VStack spacing={6}>
            <Box w="180px" h="60px">
              <Logo />
            </Box>
            <Button
              onClick={loadEmbedFiles}
              colorScheme="blue"
              size="lg"
            >
              Load Setup
            </Button>
            {parsedShareData?.pageTitle && (
              <Text color="gray.400" fontSize="sm" textAlign="center">
                {parsedShareData.pageTitle} {trackCount > 0 && `(${trackCount} ${trackCount === 1 ? 'Track' : 'Tracks'})`}
              </Text>
            )}
          </VStack>
        )}
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      h="100vh"
      w="100%"
      bg="gray.900"
      overflow="hidden"
      position="relative"
      borderRadius="2xl"
      sx={{
        borderRadius: '16px',
        backgroundColor: '#1a202c', // Explicit gray.900 color to ensure no transparency
      }}
    >
      <Box 
        flex="1" 
        overflow="hidden"
        position="relative"
      >
        <Container 
          maxW="container.xl" 
          h="full"
          pt={2}
          pb={4}
          display="flex" 
          flexDirection="column"
        >
          {/* Title and Link Button at the top */}
          <Box
            position="relative"
            w="full"
            mb={2}
            px={4}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexShrink={0}
          >
            {/* Custom Title - Left side */}
            <Box flex="1" mr={4}>
              <Text
                fontSize="sm"
                color="#FED49A"
                textShadow="0 0 10px rgba(254, 212, 154, 0.3)"
                fontWeight="bold"
                noOfLines={1}
              >
                {(() => {
                  const pageTitle = shareData?.pageTitle || parsedShareData?.pageTitle;
                  const trackCount = loadedFiles.length;
                  const trackText = `${trackCount} ${trackCount === 1 ? 'Track' : 'Tracks'}`;
                  
                  if (pageTitle) {
                    return `${pageTitle} (${trackText})`;
                  } else {
                    return trackText;
                  }
                })()}
              </Text>
            </Box>
            {/* View Full Setup Button - Right side */}
            {fullSetupUrl && (
              <Button
                as="a"
                href={fullSetupUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                colorScheme="blue"
                rightIcon={<FaExternalLinkAlt />}
                variant="outline"
                border="none"
                h="auto"
                minH="auto"
                py={1.5}
                lineHeight="1.2"
                _hover={{ border: "none" }}
                _active={{ border: "none" }}
                _focus={{ border: "none" }}
                flexShrink={0}
                ml="auto"
              >
                View full setup on ABmyMIX.com
              </Button>
            )}
          </Box>
          <VStack spacing={4} align="stretch" flex="1" minH={0}>
            {/* Players in scrolling container */}
            <Box 
              ref={containerRef}
              data-testid="player-container"
              flex="1" 
              overflowY="auto"
              overflowX="visible"
              px={4}
              py={2}
              sx={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  bg: 'gray.900',
                },
                '&::-webkit-scrollbar-thumb': {
                  bg: 'gray.700',
                  borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  bg: 'gray.600',
                },
                // Ensure scrolling behavior works
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <VStack spacing={3} align="stretch" pb={4}>
                {loadedFiles.map((loadedFile, index) => {
                  const playerNumber = index + 1;
                  return (
                    <AudioPlayer
                      key={loadedFile.id}
                      id={loadedFile.id}
                      trackNumber={playerNumber}
                      file={loadedFile.file}
                      originalUrl={loadedFile.originalUrl}
                      shareableUrl={loadedFile.shareableUrl}
                      data-player-number={playerNumber}
                      isEmbed={true}
                      onWavesurferReady={(wavesurfer) => handlePlayerReady(wavesurfer, playerNumber)}
                    />
                  );
                })}
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Transport Controls */}
      <Box 
        borderTop="1px" 
        borderColor="gray.700" 
        bg="gray.900" 
        py={1.5}
        boxShadow="0 -4px 20px rgba(0, 0, 0, 0.3)"
        position="relative"
        width="100%"
        zIndex={2}
        flexShrink={0}
        display="flex"
        alignItems="center"
      >
        <Container maxW="container.xl" position="relative">
          <EmbedTransportControls
            onPlayPause={isPlaying ? pauseAll : playAll}
            onForward={(seconds = 3) => forward(seconds)}
            onRewind={(seconds = 3) => rewind(seconds)}
            onRewindToLastStart={() => rewindToLastStart()}
            isPlaying={isPlaying}
            playerCount={loadedFiles.length}
          />
        </Container>
      </Box>
    </Flex>
  );
};

const EmbedWithSync = () => {
  const { audioElements } = useAudioContext();
  
  return (
    <SyncProvider audioElements={audioElements}>
      <EmbedContent />
    </SyncProvider>
  );
};

const Embed = () => {
  // Prevent indexing of embed pages (they're meant for embedding, not direct access)
  useEffect(() => {
    let noindexTag = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
    if (!noindexTag) {
      noindexTag = document.createElement('meta');
      noindexTag.setAttribute('name', 'robots');
      document.head.appendChild(noindexTag);
    }
    noindexTag.setAttribute('content', 'noindex, nofollow');
  }, []);

  // Disable orientation restrictions for embed page and apply rounded corner styles
  useEffect(() => {
    document.body.setAttribute('data-embed-page', 'true');
    // Also hide rotation message
    const rotationMessage = document.querySelector('.rotation-message');
    if (rotationMessage) {
      (rotationMessage as HTMLElement).style.display = 'none';
    }
    // Ensure root is visible and apply transparent background
    const root = document.getElementById('root');
    if (root) {
      root.style.display = 'block';
      root.style.backgroundColor = 'transparent';
      root.style.padding = '0';
      root.style.boxSizing = 'border-box';
    }
    // Apply transparent background to body and html - override Chakra UI theme
    document.body.style.backgroundColor = 'transparent';
    document.body.style.background = 'transparent';
    document.documentElement.style.backgroundColor = 'transparent';
    document.documentElement.style.background = 'transparent';
    
    // Also override any Chakra UI global styles by setting inline style with !important via CSS
    const style = document.createElement('style');
    style.id = 'embed-transparent-bg';
    style.textContent = `
      body[data-embed-page],
      body:has([data-embed-page]),
      html:has([data-embed-page]),
      #root:has([data-embed-page]),
      body[data-embed-page] #root,
      body[data-embed-page] > *,
      #root > * {
        background-color: transparent !important;
        background: transparent !important;
      }
      /* Ensure ChakraProvider wrapper is transparent */
      body[data-embed-page] .chakra-ui-light,
      body[data-embed-page] [data-theme] {
        background-color: transparent !important;
        background: transparent !important;
      }
      /* Ensure the main container clips to rounded corners */
      body[data-embed-page] [data-embed-page] > *:first-child,
      body[data-embed-page] #root > *:first-child {
        overflow: hidden !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.body.removeAttribute('data-embed-page');
      if (root) {
        root.style.backgroundColor = '';
        root.style.padding = '';
        root.style.boxSizing = '';
      }
      document.body.style.backgroundColor = '';
      document.body.style.background = '';
      document.documentElement.style.backgroundColor = '';
      document.documentElement.style.background = '';
      
      // Remove the injected style
      const style = document.getElementById('embed-transparent-bg');
      if (style) {
        style.remove();
      }
    };
  }, []);
  
  // Create a modified theme for embed pages with transparent body background
  const embedTheme = {
    ...theme,
    styles: {
      ...theme.styles,
      global: {
        ...theme.styles.global,
        body: {
          ...theme.styles.global.body,
          bg: 'transparent',
        },
        // Also ensure html and root are transparent
        html: {
          bg: 'transparent',
        },
        '#root': {
          bg: 'transparent',
        },
      },
    },
  };

  return (
    <Box bg="transparent" minH="100vh" w="100%" overflow="hidden">
      <ChakraProvider theme={embedTheme}>
        <SharedDataProvider>
          <AudioProvider>
            <NotesProvider>
              <DisplayNamesProvider>
                <PageTitleProvider>
                  <EmbedWithSync />
                </PageTitleProvider>
              </DisplayNamesProvider>
            </NotesProvider>
          </AudioProvider>
        </SharedDataProvider>
      </ChakraProvider>
    </Box>
  );
};

export default Embed;

